// Sport theme island (M12-01): header menu (mega menu, dropdowns, "More" for items that don't fit),
// compact sticky header, search overlay, menu drawer, header counts, announcement rotation, footer
// accordions on phones, hero slideshow, countdown boxes, sideways rails, card quick add and the
// "Added to cart" drawer. Vanilla, data-* hooks only; every part is optional and guarded, and the
// page works without it (see the section templates).
import { cartCall, setCount, toast } from "./storefront/cart.js";

const desktop = window.matchMedia("(min-width: 1024px)");
const phone = window.matchMedia("(max-width: 767px)");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

// one part failing (e.g. markup an owner rearranged) must not stop the others
const safely = (init) => {
	try {
		init();
	} catch (error) {
		console.error(error); // eslint-disable-line no-console
	}
};

// ------------------------------------------------------------------ dialogs

const canModal =
	typeof HTMLDialogElement === "function" && "showModal" in HTMLDialogElement.prototype;
let opener = null; // focus returns here when a dialog closes

function openDialog(dialog, from) {
	if (!canModal || !dialog || dialog.open) return false;
	opener = from || document.activeElement;
	dialog.showModal();
	if (from && from.getAttribute("aria-controls") === dialog.id)
		from.setAttribute("aria-expanded", "true");
	return true;
}

function initDialogs() {
	document
		.querySelectorAll("dialog[data-sp-dialog], dialog[data-sp-cart-drawer]")
		.forEach((dialog) => {
			dialog.addEventListener("close", () => {
				document
					.querySelectorAll(`[aria-controls="${dialog.id}"][aria-expanded]`)
					.forEach((el) => el.setAttribute("aria-expanded", "false"));
				if (opener && opener.isConnected) opener.focus();
				opener = null;
			});
			// a click on the backdrop (outside the dialog's box) closes it
			dialog.addEventListener("click", (event) => {
				if (event.target !== dialog) return;
				const box = dialog.getBoundingClientRect();
				const inside =
					event.clientX >= box.left &&
					event.clientX <= box.right &&
					event.clientY >= box.top &&
					event.clientY <= box.bottom;
				if (!inside) dialog.close();
			});
		});
}

// ------------------------------------------------------------------ header

function initHeader(header) {
	const nav = header.querySelector("[data-sp-nav]");
	const items = nav ? [...nav.querySelectorAll(":scope > ul > [data-sp-item]")] : [];
	const more = nav && nav.querySelector("[data-sp-more]");
	const moreList = more && more.querySelector("[data-sp-more-list]");
	header.dataset.spReady = "1";

	const closeAll = (except) =>
		header.querySelectorAll(".sp-nav__item.is-open").forEach((item) => {
			if (item === except) return;
			item.classList.remove("is-open");
			item.querySelectorAll(":scope > [data-sp-toggle]").forEach((t) =>
				t.setAttribute("aria-expanded", "false")
			);
		});

	// a dropdown sits under its item: its start edge in the header's reading direction
	const place = (item) => {
		const panel = item.querySelector(":scope > [data-sp-panel]");
		if (!panel || !panel.classList.contains("sp-panel--drop")) return;
		const box = header.getBoundingClientRect();
		const at = item.getBoundingClientRect();
		const rtl = getComputedStyle(header).direction === "rtl";
		let start = rtl ? box.right - at.right : at.left - box.left;
		start = Math.max(0, Math.min(start, box.width - Math.min(384, box.width * 0.9)));
		panel.style.setProperty("--sp-drop-start", `${Math.round(start)}px`);
	};

	const setOpen = (item, open) => {
		closeAll(item);
		if (open) place(item);
		item.classList.toggle("is-open", open);
		item.querySelectorAll(":scope > [data-sp-toggle]").forEach((t) =>
			t.setAttribute("aria-expanded", String(open))
		);
	};

	header.addEventListener("click", (event) => {
		const toggle = event.target.closest("[data-sp-toggle]");
		if (!toggle || !header.contains(toggle)) return;
		const item = toggle.closest(".sp-nav__item");
		setOpen(item, !item.classList.contains("is-open"));
	});
	header.addEventListener("pointerover", (event) => {
		if (event.pointerType !== "mouse") return;
		const item = event.target.closest(".sp-nav__item");
		if (!item || item.closest("[data-sp-more-list]")) return;
		closeAll(item); // one panel at a time (hover shows the hovered one)
		place(item);
	});
	header.addEventListener("keydown", (event) => {
		if (event.key !== "Escape") return;
		const open = header.querySelector(".sp-nav__item.is-open");
		if (!open) return;
		setOpen(open, false);
		const toggle = open.querySelector(":scope > [data-sp-toggle]");
		if (toggle) toggle.focus();
	});
	document.addEventListener("click", (event) => {
		if (!event.target.closest(".sp-nav__item")) closeAll();
	});
	header.addEventListener("focusout", (event) => {
		if (event.relatedTarget && !event.relatedTarget.closest(".sp-nav__item.is-open"))
			closeAll();
	});

	// "More": items that don't fit on the line are listed there instead (theme setting)
	const moreEntry = (item) => {
		const label = item.querySelector(":scope > .sp-nav__link");
		const li = document.createElement("li");
		if (label && label.tagName === "A") {
			const link = document.createElement("a");
			link.href = label.getAttribute("href");
			link.textContent = label.textContent;
			li.append(link);
			return li;
		}
		const name = document.createElement("span");
		name.className = "sp-drop__label";
		name.textContent = label ? label.textContent : "";
		const sub = document.createElement("ul");
		item.querySelectorAll(".sp-mega__heading[href], .sp-drop__link[href]").forEach((a) => {
			const row = document.createElement("li");
			const link = document.createElement("a");
			link.href = a.getAttribute("href");
			link.textContent = a.textContent;
			row.append(link);
			sub.append(row);
		});
		li.append(name, sub);
		return li;
	};
	const fit = () => {
		if (!nav || !more || !moreList || !("overflow" in nav.dataset)) return;
		items.forEach((item) => (item.hidden = false));
		more.hidden = true;
		moreList.replaceChildren();
		if (!desktop.matches || nav.scrollWidth <= nav.clientWidth + 1) return;
		more.hidden = false;
		const moved = [];
		for (let i = items.length - 1; i > 0 && nav.scrollWidth > nav.clientWidth + 1; i--) {
			items[i].hidden = true;
			moved.unshift(items[i]);
		}
		moreList.replaceChildren(...moved.map(moreEntry));
	};
	if (nav && "ResizeObserver" in window) new ResizeObserver(() => fit()).observe(nav);
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
	fit();

	// sticky header: things that stick below it (collection toolbar, gallery) use its height
	if (header.classList.contains("is-sticky") && "ResizeObserver" in window) {
		new ResizeObserver(([entry]) =>
			document.documentElement.style.setProperty(
				"--sp-sticky-top",
				`${Math.round(
					entry.borderBoxSize ? entry.borderBoxSize[0].blockSize : header.offsetHeight
				)}px`
			)
		).observe(header);
	}

	// compact sticky header: a zero-height sentinel just above it tells when it is stuck
	const sentinel = header.previousElementSibling;
	if ("compact" in header.dataset && sentinel && sentinel.matches("[data-sp-sentinel]")) {
		new IntersectionObserver(([entry]) =>
			header.classList.toggle("is-compact", !entry.isIntersecting)
		).observe(sentinel);
	}

	// menu drawer and search overlay (dialogs); without them the links/buttons do their default
	header.addEventListener("click", (event) => {
		const drawer = event.target.closest("[data-sp-drawer-open]");
		if (drawer && openDialog(document.getElementById("sp-drawer"), drawer)) return;
		const search = event.target.closest("[data-sp-search-open]");
		if (search) {
			const dialog = document.getElementById("sp-search");
			if (openDialog(dialog, search)) {
				event.preventDefault();
				const input = dialog.querySelector("input[name=q]");
				if (input) input.focus();
			}
		}
	});
	// the drawer is for phones: close it when the window grows to the desktop header
	desktop.addEventListener("change", (event) => {
		const drawer = document.getElementById("sp-drawer");
		if (event.matches && drawer && drawer.open) drawer.close();
		closeAll();
	});
}

// header counts: the cart bubble hides at 0; the wishlist count comes from the wishlist island
function initCounts() {
	const sync = () =>
		document
			.querySelectorAll(".sp-count[data-cart-count]")
			.forEach((el) =>
				el.classList.toggle("is-zero", (el.textContent || "").trim() === "0")
			);
	sync();
	document.addEventListener("store:cart-added", () => window.setTimeout(sync));
	document.addEventListener("store:wishlist-changed", (event) => {
		const count = Number((event.detail || {}).count) || 0;
		document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
			el.textContent = String(count);
			el.hidden = count === 0;
		});
	});
}

// ------------------------------------------------------------------ announcement marquee

// the row moves at the owner's speed (pixels per second) whatever its length and the screen width;
// the CSS animation runs without this too, with a duration the server estimated from the text
function initMarquee(root) {
	const track = root.querySelector(".sp-marquee__track");
	const speed = Number(root.dataset.speed) || 50;
	if (!track || !("ResizeObserver" in window)) return;
	new ResizeObserver(() => {
		const width = track.getBoundingClientRect().width;
		if (width)
			root.style.setProperty(
				"--sp-marquee-duration",
				`${Math.max(5, Math.round(width / speed))}s`
			);
	}).observe(track);
}

// ------------------------------------------------------------------ footer accordions (phones)

function initFooter() {
	const folds = [...document.querySelectorAll("details[data-sp-fold]")];
	if (!folds.length) return;
	const apply = () =>
		folds.forEach((fold) => {
			fold.open = !phone.matches;
			fold.dataset.ready = "1";
		});
	apply();
	phone.addEventListener("change", apply);
	// on wider screens the columns always stay open
	folds.forEach((fold) =>
		fold.querySelector("summary").addEventListener("click", (event) => {
			if (!phone.matches) event.preventDefault();
		})
	);
}

// ------------------------------------------------------------------ "Added to cart" drawer

function initCartDrawer() {
	const drawer = document.querySelector("[data-sp-cart-drawer]");
	if (!drawer) return;
	document.addEventListener("store:cart-added", (event) => {
		const result = event.detail || {};
		const set = (hook, text) => {
			const el = drawer.querySelector(hook);
			if (el) el.textContent = text == null ? "" : String(text);
		};
		set("[data-cart-drawer-count]", result.count);
		set("[data-cart-drawer-subtotal]", result.subtotal_display || "");
		set("[data-cart-note]", result.warning || "");
		if (!openDialog(drawer, document.activeElement) && !drawer.open) {
			if (!canModal) toast(result.warning || drawer.dataset.added || ""); // no <dialog> support
			return;
		}
		// the drawer says it: no toast on top of it (the product page's add-to-cart shows one)
		const shown = document.querySelector(".toast");
		if (shown) window.setTimeout(() => (shown.hidden = true));
	});
}

// ------------------------------------------------------------------ hero slideshow

function initSlideshow(root) {
	const slides = [...root.querySelectorAll("[data-sp-slide]")];
	const controls = root.querySelector("[data-sp-slide-controls]");
	if (slides.length < 2 || !controls) return;
	const live = root.querySelector(".sp-hero__slides");
	const dots = [...controls.querySelectorAll("[data-sp-slide-to]")];
	const pause = controls.querySelector("[data-sp-slide-pause]");
	const label = pause && pause.querySelector("[data-label]");
	const seconds = parseInt(root.dataset.autoplay, 10) || 0;
	let current = 0;
	let timer = 0;
	let held = false;

	const show = (index) => {
		current = (index + slides.length) % slides.length;
		slides.forEach((slide, i) => {
			slide.hidden = i !== current;
			// the next pictures load lazily: start loading once their slide shows
			if (i === current)
				slide
					.querySelectorAll("img[loading=lazy]")
					.forEach((img) => (img.loading = "eager"));
		});
		dots.forEach((dot, i) => {
			if (i === current) dot.setAttribute("aria-current", "true");
			else dot.removeAttribute("aria-current");
		});
	};
	const setLabel = (playing) => {
		if (!pause) return;
		pause.classList.toggle("is-paused", !playing);
		if (label) label.textContent = playing ? pause.dataset.pause : pause.dataset.play;
	};
	const stop = () => {
		window.clearInterval(timer);
		timer = 0;
		live.setAttribute("aria-live", "polite"); // slide changes are announced only while stopped
		setLabel(false);
	};
	const start = () => {
		if (!seconds || reducedMotion.matches) return stop();
		window.clearInterval(timer);
		timer = window.setInterval(() => !held && show(current + 1), seconds * 1000);
		live.setAttribute("aria-live", "off");
		setLabel(true);
	};

	controls.hidden = false;
	if (!seconds && pause) pause.hidden = true;
	controls.addEventListener("click", (event) => {
		const button = event.target.closest("button");
		if (!button) return;
		if (button.matches("[data-sp-slide-pause]")) return timer ? stop() : start();
		stop(); // a shopper who steps through the slides reads them at their own pace
		if (button.matches("[data-sp-slide-to]")) show(Number(button.dataset.spSlideTo) || 0);
		else show(current + (button.matches("[data-sp-slide-prev]") ? -1 : 1));
	});
	// pointer or keyboard focus inside holds the rotation (WCAG 2.2.2)
	root.addEventListener("pointerenter", () => (held = true));
	root.addEventListener("pointerleave", () => (held = false));
	root.addEventListener("focusin", (event) => {
		if (event.target !== pause && timer) stop();
	});
	// swipe on touch screens: the reading direction decides which way is "next"
	let startX = null;
	root.addEventListener("pointerdown", (event) => {
		if (event.pointerType !== "mouse") startX = event.clientX;
	});
	root.addEventListener("pointerup", (event) => {
		if (startX === null) return;
		const moved = event.clientX - startX;
		startX = null;
		if (Math.abs(moved) < 40) return;
		const rtl = getComputedStyle(root).direction === "rtl";
		stop();
		show(current + (moved < 0 !== rtl ? 1 : -1));
	});
	root.addEventListener("pointercancel", () => (startX = null));
	show(0);
	start();
}

// ------------------------------------------------------------------ countdown boxes

function initCountdown(el) {
	if (el.dataset.ready) return; // already run by the core island
	el.dataset.ready = "1";
	const end = Date.parse(el.dataset.countdown);
	if (Number.isNaN(end)) return;
	const labels = (el.dataset.labels || "").split("|");
	let timer = 0;
	const tick = () => {
		const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
		const section = el.closest(".s-countdown");
		if (!left) {
			window.clearInterval(timer);
			if ("hideEnded" in el.dataset && section) section.hidden = true;
			el.textContent = el.dataset.ended || "";
			if (section) section.querySelectorAll(".sp-promo__button").forEach((b) => b.remove());
			return;
		}
		const parts = [
			Math.floor(left / 86400),
			Math.floor((left % 86400) / 3600),
			Math.floor((left % 3600) / 60),
			left % 60,
		];
		el.replaceChildren(
			...parts.map((value, i) => {
				const box = document.createElement("span");
				box.className = "s-countdown__part sp-promo__part";
				const number = document.createElement("span");
				number.className = "sp-promo__number";
				number.textContent = i ? String(value).padStart(2, "0") : String(value);
				const name = document.createElement("span");
				name.className = "sp-promo__label";
				name.textContent = labels[i] || "";
				box.append(number, " ", name);
				return box;
			})
		);
	};
	timer = window.setInterval(tick, 1000);
	tick();
}

// ------------------------------------------------------------------ sideways rails

function initRail(rail) {
	const track = rail.querySelector("[data-sp-rail-track]");
	const buttons = rail.querySelector("[data-sp-rail-buttons]");
	if (!track || !buttons) return;
	const prev = buttons.querySelector("[data-sp-rail-prev]");
	const next = buttons.querySelector("[data-sp-rail-next]");
	const rtl = () => getComputedStyle(track).direction === "rtl";
	// scrollLeft runs negative in right-to-left rows: compare distances from the start edge
	const update = () => {
		const overflow = track.scrollWidth - track.clientWidth > 2;
		buttons.hidden = !overflow;
		if (!overflow) return;
		const from = Math.abs(track.scrollLeft);
		prev.disabled = from < 2;
		next.disabled = from > track.scrollWidth - track.clientWidth - 2;
	};
	const step = (direction) => {
		const amount = Math.max(track.clientWidth * 0.8, 200) * direction * (rtl() ? -1 : 1);
		track.scrollBy({ left: amount, behavior: reducedMotion.matches ? "auto" : "smooth" });
	};
	prev.addEventListener("click", () => step(-1));
	next.addEventListener("click", () => step(1));
	track.addEventListener("scroll", update, { passive: true });
	if ("ResizeObserver" in window) new ResizeObserver(update).observe(track);
	update();
}

// ------------------------------------------------------------------ quick add on cards

function initQuickAdd() {
	document.addEventListener("submit", async (event) => {
		const form = event.target.closest("[data-sp-quick-add]");
		if (!form) return;
		event.preventDefault();
		const button = form.querySelector("[type=submit]");
		// busy, not disabled: a disabled button loses the keyboard focus
		if (!button || button.getAttribute("aria-disabled") === "true") return;
		button.setAttribute("aria-disabled", "true");
		try {
			const result = await cartCall("add", {
				item_code: form.elements.item_code.value,
				qty: (form.elements.qty && form.elements.qty.value) || 1,
				lang: document.documentElement.lang,
			});
			setCount(result.count);
			button.focus();
			document.dispatchEvent(new CustomEvent("store:cart-added", { detail: result }));
		} catch (e) {
			toast(e.message || form.dataset.failed);
		} finally {
			button.removeAttribute("aria-disabled");
		}
	});
}

// ------------------------------------------------------------------ product page

// thumbnails show their picture in the main image (wider screens); on phones the pictures swipe
// and the counter follows. product.js may switch the main image to a variant's picture.
function initGallery(gallery) {
	const main = gallery.querySelector("[data-product-image]");
	const track = gallery.querySelector("[data-sp-gallery-track]");
	const thumbs = [...gallery.querySelectorAll("[data-sp-thumb]")];
	const index = gallery.querySelector("[data-sp-gallery-index]");
	if (!main) return;
	const mark = (active) =>
		thumbs.forEach((thumb) => {
			if (thumb === active) thumb.setAttribute("aria-current", "true");
			else thumb.removeAttribute("aria-current");
		});
	gallery.addEventListener("click", (event) => {
		const thumb = event.target.closest("[data-sp-thumb]");
		if (!thumb) return;
		main.src = thumb.dataset.spThumb;
		main.removeAttribute("srcset");
		main.alt = thumb.dataset.alt || main.alt;
		mark(thumb);
	});
	if (track && index) {
		track.addEventListener(
			"scroll",
			() => {
				const width = track.clientWidth || 1;
				index.textContent = String(Math.round(Math.abs(track.scrollLeft) / width) + 1);
			},
			{ passive: true }
		);
	}
}

function initQuantity(root) {
	const input = root.querySelector("input[name=qty]");
	if (!input) return;
	root.querySelectorAll("[data-sp-qty-step]").forEach((button) => {
		button.hidden = false;
		button.addEventListener("click", () => {
			const value = (parseInt(input.value, 10) || 1) + Number(button.dataset.spQtyStep);
			input.value = String(
				Math.min(Math.max(value, Number(input.min) || 1), Number(input.max) || 99)
			);
		});
	});
}

// phones: once the buy button scrolls out of view a bar with the price and the button sticks to
// the bottom; its button presses the real one, or scrolls to the options when one is missing
function initBuyBar(bar) {
	const product = bar.closest("[data-product]");
	const button = product && product.querySelector("[data-sp-buy-button]");
	const form = button && button.form;
	if (!button || !form || !("IntersectionObserver" in window)) return;
	const price = product.querySelector("[data-product-price]");
	const barPrice = bar.querySelector("[data-sp-buybar-price]");
	const barButton = bar.querySelector("[data-sp-buybar-button]");
	const label = barButton.textContent;
	const code = form.querySelector("[data-item-code]");
	const sync = () => {
		if (price && barPrice) barPrice.textContent = price.textContent;
		barButton.disabled = button.disabled;
		barButton.textContent = code && !code.value ? barButton.dataset.choose || label : label;
	};
	new IntersectionObserver(([entry]) => {
		// above the screen: the shopper has scrolled past it (not yet reached: no bar)
		const show = !entry.isIntersecting && entry.boundingClientRect.top < 0 && phone.matches;
		sync();
		bar.hidden = !show;
		document.documentElement.classList.toggle("sp-has-buybar", show);
	}).observe(button);
	new MutationObserver(sync).observe(form, {
		subtree: true,
		attributes: true,
		attributeFilter: ["disabled", "value"],
	});
	barButton.addEventListener("click", () => {
		if (code && !code.value) {
			const options = form.querySelector("fieldset[data-attribute]");
			if (options) {
				options.scrollIntoView({
					behavior: reducedMotion.matches ? "auto" : "smooth",
					block: "center",
				});
				const first = options.querySelector("input");
				if (first) first.focus({ preventScroll: true });
			}
			return;
		}
		button.click();
	});
}

safely(initDialogs);
document
	.querySelectorAll("[data-sp-header]")
	.forEach((header) => safely(() => initHeader(header)));
safely(initCounts);
document.querySelectorAll("[data-sp-marquee]").forEach((root) => safely(() => initMarquee(root)));
safely(initFooter);
safely(initCartDrawer);
safely(initQuickAdd);
document
	.querySelectorAll("[data-sp-slideshow]")
	.forEach((root) => safely(() => initSlideshow(root)));
document.querySelectorAll("[data-sp-countdown]").forEach((el) => safely(() => initCountdown(el)));
document.querySelectorAll("[data-sp-rail]").forEach((rail) => safely(() => initRail(rail)));
document
	.querySelectorAll("[data-sp-gallery]")
	.forEach((gallery) => safely(() => initGallery(gallery)));
document.querySelectorAll("[data-sp-qty]").forEach((root) => safely(() => initQuantity(root)));
document.querySelectorAll("[data-sp-buybar]").forEach((bar) => safely(() => initBuyBar(bar)));
